/**
 * FLIPPED CLASSROOM (M5) — CLASS PLAN editor (teacher/admin).
 *
 * For a `class_session`, pick modules from the cohort's COURSE and tag them
 * before/during/after. Reuses the CourseModuleRegistry (module directory
 * with resolved title). Local-first writing → write-path (cohort manager
 * authz + `academy.flipped_sessions` gate). Soft-delete with "Undo".
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Plus, Trash2 } from "@fleet/icons";
import {
  addSessionModuleMutation,
  directoryRowTitle,
  isPlanPhase,
  type PlanPhase,
  removeSessionModuleMutation,
  restoreSessionModuleMutation,
  setSessionModulePhaseMutation,
} from "@space/shared";
import { useUndo } from "@/lib/undo";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useCourseModuleDirectory, useSessionPlanItems } from "../hooks/use-session-plan";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

const PHASE_LABELS: { value: PlanPhase; label: MessageDescriptor }[] = [
  { value: "pre", label: msg`Before` },
  { value: "live", label: msg`During` },
  { value: "post", label: msg`After` },
];

export const SessionPlanEditor: Component<{
  sessionId: string;
  cohortId: string;
  organizationId: string;
  campusId: string | null;
  courseId: string;
}> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { registerUndo } = useUndo();

  const directory = useCourseModuleDirectory(() => props.courseId);
  const items = useSessionPlanItems(() => props.sessionId);

  const [moduleId, setModuleId] = createSignal("");
  const [phase, setPhase] = createSignal<PlanPhase>("pre");

  // Course modules that are NOT yet in the plan (avoids duplicates; the
  // server's UNIQUE also guarantees it).
  const inPlan = createMemo(() => new Set((items.data ?? []).map((i) => i.module_id)));
  const available = createMemo(() => (directory.data ?? []).filter((d) => !inPlan().has(d.id)));

  const titleFor = (id: string | null): string => {
    const row = (directory.data ?? []).find((d) => d.id === id);
    return (row && directoryRowTitle(row)) || t(msg`Module`);
  };

  const phaseLabel = (p: string | null) =>
    t(PHASE_LABELS.find((x) => x.value === p)?.label ?? msg`Before`);

  const addModule = async () => {
    const db = drizzle();
    const mid = moduleId();
    if (!db || !mid) {
      return;
    }
    // Position = at the end of its phase.
    const samePhase = (items.data ?? []).filter((i) => i.phase === phase());
    const position = samePhase.length;
    await addSessionModuleMutation(
      db,
      {
        sessionId: props.sessionId,
        cohortId: props.cohortId,
        organizationId: props.organizationId,
        campusId: props.campusId,
      },
      { moduleId: mid, phase: phase(), position },
    );
    setModuleId("");
  };

  const changePhase = async (id: string, next: string) => {
    const db = drizzle();
    if (!db || !isPlanPhase(next)) {
      return;
    }
    try {
      await setSessionModulePhaseMutation(db, id, next, undefined, props.organizationId);
    } catch (err) {
      console.error("Error changing phase:", err);
      showToast(t(msg`Could not move the item`), "error");
    }
  };

  const remove = (id: string) => {
    const db = drizzle();
    if (!db) {
      return;
    }
    // Captured OUTSIDE the undo closure (solid/reactivity): reading
    // `props.organizationId` inside the function passed to `registerUndo` is
    // not in a tracked scope.
    // oxlint-disable-next-line prefer-destructuring
    const organizationId = props.organizationId;
    // `registerUndo` already covers the undo's own failure; what's missing is
    // the initial delete: `patchRowVerified` can throw and this is fire-and-forget.
    void (async () => {
      try {
        await removeSessionModuleMutation(db, id, organizationId);
      } catch (err) {
        console.error("Error removing from class plan:", err);
        showToast(t(msg`Could not remove the item`), "error");
        return;
      }
      registerUndo(t(msg`Removed from the class plan`), async () => {
        const d = drizzle();
        if (d) {
          await restoreSessionModuleMutation(d, id, organizationId);
        }
      });
    })();
  };

  return (
    <div
      class="space-y-3 rounded-xl border border-border bg-muted/10 p-3"
      data-testid="session-plan-editor"
    >
      <h4 class="text-xs font-bold uppercase tracking-wide text-muted-foreground">
        {t(msg`Class plan (before / during / after)`)}
      </h4>

      {/* Add module to the plan */}
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
        <select
          class={field}
          data-testid="plan-add-module"
          value={moduleId()}
          onChange={(e) => setModuleId(e.currentTarget.value)}
        >
          <option value="">{t(msg`Pick a module…`)}</option>
          <For each={available()}>
            {(d) => <option value={d.id}>{directoryRowTitle(d) ?? t(msg`Module`)}</option>}
          </For>
        </select>
        <select
          class={field}
          data-testid="plan-add-phase"
          value={phase()}
          onChange={(e) => {
            const v = e.currentTarget.value;
            if (isPlanPhase(v)) {
              setPhase(v);
            }
          }}
        >
          <For each={PHASE_LABELS}>{(p) => <option value={p.value}>{t(p.label)}</option>}</For>
        </select>
        <Button data-testid="plan-add-btn" onClick={() => void addModule()} class="gap-1">
          <Plus class="h-4 w-4" /> {t(msg`Add`)}
        </Button>
      </div>

      {/* Current plan items */}
      <Show
        when={(items.data ?? []).length > 0}
        fallback={
          <p class="text-xs text-muted-foreground">{t(msg`No modules in the plan yet.`)}</p>
        }
      >
        <ul class="space-y-1">
          <For each={items.data ?? []}>
            {(i) => (
              <li
                class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-sm"
                data-testid="plan-editor-item"
                data-module-id={i.module_id ?? ""}
                data-phase={i.phase ?? ""}
              >
                <span class="truncate">{titleFor(i.module_id)}</span>
                <span class="flex items-center gap-2">
                  <select
                    class="rounded-lg border border-border bg-field-background px-2 py-1 text-xs"
                    data-testid="plan-item-phase"
                    value={i.phase ?? "pre"}
                    aria-label={t(msg`Module phase`)}
                    onChange={(e) => void changePhase(i.id, e.currentTarget.value)}
                  >
                    <For each={PHASE_LABELS}>
                      {(p) => <option value={p.value}>{t(p.label)}</option>}
                    </For>
                  </select>
                  <span class="text-xs text-muted-foreground">{phaseLabel(i.phase)}</span>
                  <button
                    type="button"
                    data-testid="plan-item-remove"
                    onClick={() => remove(i.id)}
                    class="text-muted-foreground hover:text-danger"
                    aria-label={t(msg`Remove from plan`)}
                  >
                    <Trash2 class="h-4 w-4" />
                  </button>
                </span>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
};
